import { Box, ModalCloseButton, ModalContent, ModalHeader } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { FormikCheckbox, FormikSelect } from 'components/Formik';
import { ControllableModal } from 'components/Modal';
import {
  IQuickbookDesktopServiceItemData,
  useSaveQuickbookDesktopServiceItem,
} from 'hooks/integrations/quickbookDesktop/useSaveQuickbookDesktopServiceItem';
import { useFetchPayrollOptions } from 'hooks/integrations/useFetchPayrollOptions';

import { initialServiceItemFormData } from './schema';

interface IServiceItemFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  formData: typeof initialServiceItemFormData;
}

const ServiceItemFormModal: React.FC<IServiceItemFormModalProps> = ({ isOpen, onClose, formData }) => {
  const { t } = useTranslation(['employees', 'common', 'integrations']);

  const { status: qbdPayrollStatus, data: qbdPayrollData } = useFetchPayrollOptions();

  const { status: qbdSaveServiceItemStatus, mutateAsync: qbdSaveServiceItemMutateAsync } =
    useSaveQuickbookDesktopServiceItem();

  const handleFormSubmit = async (values: typeof initialServiceItemFormData) => {
    const postData: IQuickbookDesktopServiceItemData = {
      service_item_id: values.service_item_id,
      billable: values.billable.includes('1') ? true : false,
      double_overtime_payroll_item: values.double_overtime_payroll_item?.value,
      overtime_payroll_item: values.overtime_payroll_item?.value,
      reg_payroll_item: values.reg_payroll_item?.value,
    };
    qbdSaveServiceItemMutateAsync(postData).then(() => {
      onClose();
    });
  };

  return (
    <ControllableModal isOpen={isOpen} onClose={onClose} size="md">
      <ModalContent p="6" pt="2" textAlign="center">
        <ModalCloseButton />
        <ModalHeader mb="4">{t('integrations:quickbooksDesktop.updateServiceItem')}</ModalHeader>
        <Formik initialValues={formData} enableReinitialize onSubmit={handleFormSubmit}>
          {() => (
            <Form noValidate>
              <Box>
                <FormikSelect
                  name="reg_payroll_item"
                  label={t('integrations:regularPayrollItem')}
                  options={qbdPayrollData?.payload}
                  selectProps={{
                    placeholder: 'Select payroll item',
                    isClearable: true,
                    isLoading: qbdPayrollStatus === 'loading',
                  }}
                />
              </Box>
              <Box>
                <FormikSelect
                  name="overtime_payroll_item"
                  label={t('integrations:overtimePayrollItem')}
                  options={qbdPayrollData?.payload}
                  selectProps={{
                    placeholder: 'Select payroll item',
                    isClearable: true,
                    isLoading: qbdPayrollStatus === 'loading',
                  }}
                />
              </Box>
              <Box>
                <FormikSelect
                  name="double_overtime_payroll_item"
                  label={t('integrations:doublePayrollItem')}
                  options={qbdPayrollData?.payload}
                  selectProps={{
                    placeholder: 'Select payroll item',
                    isClearable: true,
                    isLoading: qbdPayrollStatus === 'loading',
                  }}
                />
              </Box>
              <Box display="flex">
                <FormikCheckbox options={[{ label: t('common:billable'), value: '1' }]} name="billable" />
              </Box>

              <Button
                type="submit"
                colorScheme="secondary"
                isLoading={qbdSaveServiceItemStatus === 'loading'}
                minWidth="100px"
              >
                {t('common:save')}
              </Button>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </ControllableModal>
  );
};
export default ServiceItemFormModal;
